import { useEffect, useState } from "react";
import { openExternal } from "../utils/openExternal";
import { Link } from "react-router-dom";
import { db } from "../services/firebase";
import { useAuth } from "../context/AuthContext";
import { collection, query, onSnapshot, orderBy, where, doc } from "firebase/firestore";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer,
} from "recharts";
import { FileDown, Loader2 } from "lucide-react";
import { usePdfRiport } from "../hooks/usePdfRiport";
import {
  formatKgTick,
  formatGlucoseTick,
  formatPercentTick,
  formatWeekTick,
} from "../utils/chartFormat";
import { entryTimestampMs, sortEntriesByDateAsc, formatEntryDateLabel, isFutureEntry, selectEntriesEmptyReason, ENTRIES_EMPTY_MESSAGES } from "../utils/entryDate";
import { resolveBaseWeight, calcTotalLoss, calcTotalLossPct, selectWeighedEntries, selectGlucoseEntries, formatWeightChange, formatWeightChangePct, weightChangeLabel } from "../utils/therapyStats";
import { selectLastLoggedDose } from "../hooks/useLastLoggedDose";
import { selectClinicalBranch, selectClosestDose, interpolateWeeklySeries, selectClinicalEmptyStateReason, formatReferenceDoseLabel, formatComparatorLabel, formatDerivedNote } from "../utils/clinicalReferenceSelect";
import { IS_NATIVE } from "../config/platform";

interface Entry {
  id: string;
  // A D2 döntés óta hiányozhat (dózis-only bejegyzés): a drótról `null`
  // jön. Súly-idősorhoz mindig selectWeighedEntries.
  weight?: number | null;
  dose?: number;
  date?: string;
  mellekhat?: Record<string, number>;
  createdAt?: { seconds: number };
  glucose?: number | null;
}

interface Profil {
  gyogyszer?: string;
  kezdoSuly?: string;
  celSuly?: string;
  terapiaKezdete?: string;
  glucoseEnabled?: boolean;
  therapyReason?: "cukorbetegseg" | "testsulycsokkenes" | "mindketto" | null;
}

type TimeRange = "2w" | "1m" | "3m" | "all";

const RANGE_LABELS: Record<TimeRange, string> = {
  "2w": "2 hét", "1m": "1 hónap", "3m": "3 hónap", "all": "Összes"
};

export default function Statisztika() {
  const { user } = useAuth();
  const [entries, setEntries] = useState<Entry[]>([]);
  const [profil, setProfil] = useState<Profil>({});
  const [range, setRange] = useState<TimeRange>("all");
  const [showDoses, setShowDoses] = useState(true);
  const { generate, generating } = usePdfRiport();

  useEffect(() => {
    if (!user) return;
    const q = query(
      collection(db, "entries"),
      where("userId", "==", user.uid),
      orderBy("createdAt", "asc")
    );
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const docs = snapshot.docs.map(d => ({
        id: d.id,
        weight: d.data().weight,
        dose: d.data().dose,
        date: d.data().date,
        mellekhat: d.data().mellekhat,
        createdAt: d.data().createdAt,
        glucose: d.data().glucose,
      })) as Entry[];
      // A Firestore-lekérdezés createdAt (beviteli idő) szerint rendez —
      // ez visszamenőleges rögzítésnél rossz sorrendet ad. Minden idősor
      // a date (mikor történt) szerint rendezve legyen.
      setEntries(sortEntriesByDateAsc(docs));
    });
    return () => unsubscribe();
  }, [user]);

  // A meglévő aiEnabled onSnapshot-mintát követi (App.tsx) — a glucoseEnabled
  // kapcsoló azonnal érvényesül, oldalújratöltés nélkül.
  useEffect(() => {
    if (!user) return;
    const unsubscribe = onSnapshot(doc(db, "profiles", user.uid), (snap) => {
      if (snap.exists()) setProfil(snap.data() as Profil);
    });
    return () => unsubscribe();
  }, [user]);

  const now = Date.now() / 1000;
  const rangeSecs: Record<TimeRange, number> = {
    "2w": 14 * 86400, "1m": 30 * 86400, "3m": 90 * 86400, "all": Infinity
  };

  // Jövőbeli dátumú bejegyzés (elgépelt évszám/nap) kizárva minden
  // számításból/grafikonból — a Napló-listában továbbra is látszik, csak a
  // Statisztikán számító tömbökből esik ki (2026-08-17 QA-lelet). A PDF
  // (usePdfRiport → GlpRiportPdf) szándékosan a nyers `entries`-t kapja meg
  // itt lent, mert a riport táblázata a teljes naplót mutatja — a
  // GlpRiportPdf saját maga szűri ki a jövőbeli bejegyzéseket az
  // összegző mezőiből.
  const computableEntries = entries.filter(e => !isFutureEntry(e));

  // Az időszak-szűrő a date (mikor történt) alapján dönt, nem a createdAt
  // (mikor íródott be) alapján — enélkül egy ma visszamenőlegesen felvitt
  // egész éves adatsor mindegyike "2 hét"-ként jelent volna meg.
  const filtered = computableEntries.filter(e => (now - entryTimestampMs(e) / 1000) <= rangeSecs[range]);

  // Csak a ténylegesen MÉRT bejegyzések: a `!== undefined` szűrő a
  // `null`-t (súly nélkül mentett bejegyzés) átengedte volna, és onnantól
  // az "Összes fogyás" üresre vált, ha az időszak utolsó bejegyzése
  // dózis-only.
  const validEntries = selectWeighedEntries(filtered);
  const firstWeight = validEntries[0]?.weight;
  const lastWeight = validEntries[validEntries.length - 1]?.weight;
  // "Összes" nézetben a profil kezdő súlya a kanonikus bázis (ugyanaz, mint a
  // Fooldal "Összes fogyás" kártyája és a PDF-riport) — szűkebb időszaknál az
  // adott időszak eleje a helyes bázis ("mennyit fogytam az utolsó 2 hétben"),
  // nem a lifetime kezdő súly. Közös forrás: resolveBaseWeight (docs/FOGALMAK.md).
  const kezdoSulyProfil = resolveBaseWeight(profil.kezdoSuly, firstWeight, range === "all");
  const totalLoss = calcTotalLoss(kezdoSulyProfil, lastWeight);
  const totalLossPct = calcTotalLossPct(kezdoSulyProfil, totalLoss);

  // Súlygrafikon adatok — dózis jelölőkkel
  const weightData = validEntries.map((e) => ({
    date: formatEntryDateLabel(e),
    weight: e.weight,
    dose: e.dose && e.dose > 0 ? e.dose : null,
  }));

  // "Nap 0-tól" a legkorábbi bejegyzés valós date-jétől indul, nem a
  // tömbindextől (ami visszamenőleges rögzítésnél a beviteli sorrendet
  // tükrözné, nem az eltelt valós időt — 2026-08-15 éles hiba).
  const baseDateMs = validEntries[0] ? entryTimestampMs(validEntries[0]) : 0;

  // Trial % — ugyanígy valós eltelt hét, nem tömbindex-alapú közelítés.
  // FIGYELEM: ez SZÁNDÉKOSAN NEM a kanonikus kezdoSulyProfil/resolveBaseWeight
  // (fent) — a klinikai trial-görbével való összevetéshez mindig a szűrt
  // (filtered) időszak első bejegyzése a bázis, sosem a profil lifetime
  // kezdő súlya, függetlenül a range-től. Ne vond össze a kettőt egy
  // refaktornál — más kérdésre válaszolnak ("mennyit fogytam ehhez képest,
  // hogy állok a klinikai vizsgálathoz képest" vs. "mennyit fogytam
  // összesen a terápia kezdete óta").
  const baseWeight = validEntries[0]?.weight || 0;
  const userTrialData = validEntries.map((e) => {
    const dayOffset = baseDateMs ? (entryTimestampMs(e) - baseDateMs) / 86400000 : 0;
    return {
      week: Math.round(dayOffset / 7),
      yourPct: baseWeight && e.weight ? ((e.weight - baseWeight) / baseWeight) * 100 : 0,
    };
  });

  // Klinikai referencia-ág kiválasztása — docs/KLINIKAI_ADATOK.md, egyetlen
  // forrás: src/data/clinicalReference.ts. Hiányzó/kétértelmű gyógyszer vagy
  // terápia-ok esetén NINCS görbe (üres állapot jobb, mint rossz referencia).
  const clinicalBranch = selectClinicalBranch(profil.gyogyszer, profil.therapyReason);
  // B3 (külső audit, 2026-10-14): a jelenlegi dózis a kanonikus
  // selectLastLoggedDose-ból jön, a TELJES (nem jövőbeli) naplóból — ahogy a
  // Főoldal is. Korábban csak a súlyos és időszakon belüli bejegyzésekből
  // számolt, így egy súly nélküli injekció után a klinikai görbe egy régebbi
  // dózishoz illesztett, mint amit a Főoldal mutatott.
  const lastDose = selectLastLoggedDose(computableEntries) ?? undefined;
  const matchedDose = clinicalBranch ? selectClosestDose(clinicalBranch, lastDose) : undefined;
  const referenceSeries = clinicalBranch && matchedDose
    ? interpolateWeeklySeries(clinicalBranch.weeks, matchedDose.pctChange)
    : [];
  const comparatorSeries = clinicalBranch
    ? interpolateWeeklySeries(clinicalBranch.weeks, clinicalBranch.comparatorPct)
    : [];
  const clinicalEmptyReason = !clinicalBranch
    ? selectClinicalEmptyStateReason(profil.gyogyszer, profil.therapyReason)
    : null;

  // Éhomi vércukor adatok — kizárólag rögzítés/megjelenítés, minősítés,
  // célsáv, riasztás vagy trendből levont következtetés nélkül (CLAUDE.md
  // 2. szabály: az app nem lehet orvostechnikai eszköz).
  // A vércukor a súlytól FÜGGETLEN: a csak vércukros bejegyzés (D2) is
  // a görbére kerül (B3, 2026-10-14) — a PDF-táblázat eddig is mutatta.
  const glucoseData = selectGlucoseEntries(filtered)
    .map(e => ({
      date: formatEntryDateLabel(e),
      glucose: e.glucose as number,
    }));

  // Dózisváltás referencia vonalak
  const doseChanges = weightData.reduce<{ date: string; dose: number }[]>((acc, d, i) => {
    if (d.dose && (i === 0 || weightData[i - 1].dose !== d.dose)) {
      acc.push({ date: d.date, dose: d.dose });
    }
    return acc;
  }, []);

  const CustomDot = (props: any) => {
    const { cx, cy, payload } = props;
    if (!payload.dose || !showDoses) return null;
    const colors: Record<number, string> = {
      2.5: "#9CA3AF", 5: "#7C3AED", 7.5: "#059669",
      10: "#2563EB", 12.5: "#0E7490", 15: "#0F172A",
    };
    const color = colors[payload.dose] || "#6B7280";
    return (
      <g>
        <circle cx={cx} cy={cy} r={6} fill={color} stroke="white" strokeWidth={2} />
      </g>
    );
  };

  return (
    <div className="max-w-2xl mx-auto px-4 pt-6 pb-6 space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Statisztika</h1>
          <p className="text-sm text-gray-500 mt-0.5">Haladásod áttekintése</p>
        </div>
        <button
          onClick={() => generate(entries, profil, user?.email ?? "")}
          disabled={generating || entries.length === 0}
          className="flex items-center gap-1.5 bg-[#1a5c46] text-white text-xs font-semibold px-3 py-2 rounded-xl hover:bg-[#154a38] transition-colors disabled:opacity-50"
        >
          {generating
            ? <Loader2 size={13} className="animate-spin" />
            : <FileDown size={13} />}
          PDF riport
        </button>
      </div>

      {/* Időszűrő */}
      <div className="flex gap-1 bg-gray-100 p-1 rounded-xl">
        {(Object.keys(RANGE_LABELS) as TimeRange[]).map((r) => (
          <button key={r} onClick={() => setRange(r)}
            className={`flex-1 py-1.5 rounded-lg text-xs font-medium transition-all ${
              range === r ? "bg-white text-gray-900 shadow-sm" : "text-gray-500 hover:text-gray-700"
            }`}
          >
            {RANGE_LABELS[r]}
          </button>
        ))}
      </div>

      {/* Összegzés kártyák */}
      {validEntries.length > 1 && (
        <div className="grid grid-cols-3 gap-3">
          <div className="bg-white rounded-2xl p-3 border border-gray-100 shadow-sm text-center">
            <p className="text-xs text-gray-500 mb-1">Kezdő súly</p>
            <p className="text-lg font-bold text-gray-800">{kezdoSulyProfil} kg</p>
          </div>
          <div className="bg-white rounded-2xl p-3 border border-gray-100 shadow-sm text-center">
            <p className="text-xs text-gray-500 mb-1">Jelenlegi</p>
            <p className="text-lg font-bold text-gray-800">{lastWeight} kg</p>
          </div>
          <div className="bg-emerald-50 rounded-2xl p-3 border border-emerald-100 shadow-sm text-center">
            <p className="text-xs text-emerald-600 mb-1">{weightChangeLabel(totalLoss, "period")}</p>
            {/* Növekedésnél semleges szín (B2, 2. szabály) — a szöveg előjele mondja meg az irányt. */}
            <p className={`text-lg font-bold ${formatWeightChange(totalLoss).isGain ? "text-gray-800" : "text-emerald-700"}`}>{formatWeightChange(totalLoss).text}</p>
            {formatWeightChangePct(totalLossPct) && <p className="text-xs text-emerald-500">{formatWeightChangePct(totalLossPct)}</p>}
          </div>
        </div>
      )}

      {validEntries.length < 2 && (
        <div className="bg-gray-50 rounded-2xl p-8 text-center text-gray-400 text-sm">
          {ENTRIES_EMPTY_MESSAGES[selectEntriesEmptyReason(entries.length, computableEntries.length, validEntries.length)]}
        </div>
      )}

      {validEntries.length >= 2 && (
        <>
          {/* ─ Súlygrafikon dózis jelölőkkel ─ */}
          <div className="bg-white rounded-2xl p-4 border border-gray-100 shadow-sm">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-sm font-semibold text-gray-700">Súly változás</h2>
              <button
                onClick={() => setShowDoses(!showDoses)}
                className={`text-xs px-3 py-1 rounded-lg border transition-all ${
                  showDoses ? "bg-gray-800 text-white border-gray-800" : "bg-white text-gray-500 border-gray-200"
                }`}
              >
                {showDoses ? "Dózisok elrejtése" : "Dózisok mutatása"}
              </button>
            </div>

            {showDoses && doseChanges.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-3">
                {[{d:2.5,c:"#9CA3AF"},{d:5,c:"#7C3AED"},{d:7.5,c:"#059669"},{d:10,c:"#2563EB"},{d:12.5,c:"#0E7490"},{d:15,c:"#0F172A"}]
                  .filter(x => doseChanges.some(dc => dc.dose === x.d))
                  .map(({ d, c }) => (
                    <div key={d} className="flex items-center gap-1">
                      <div className="w-3 h-3 rounded-full border-2 border-white shadow" style={{ background: c }} />
                      <span className="text-xs text-gray-500">{d} mg</span>
                    </div>
                  ))}
              </div>
            )}

            <ResponsiveContainer width="100%" height={220}>
              <LineChart data={weightData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="date" tick={{ fontSize: 10 }} interval="preserveStartEnd" />
                <YAxis domain={["dataMin - 1", "dataMax + 1"]} tick={{ fontSize: 10 }} width={58} tickFormatter={formatKgTick} />
                <Tooltip
                  contentStyle={{ fontSize: 12, borderRadius: 8, border: "1px solid #e5e7eb" }}
                  formatter={(v: any) => [formatKgTick(v), "Súly"]}
                />
                <Line
                  type="monotone" dataKey="weight"
                  stroke="#059669" strokeWidth={2.5}
                  dot={<CustomDot />}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>

          {/* ─ Klinikai összehasonlítás — docs/KLINIKAI_ADATOK.md ─ */}
          <div className="bg-white rounded-2xl p-4 border border-gray-100 shadow-sm">
            {!clinicalBranch || !matchedDose ? (
              <>
                <h2 className="text-sm font-semibold text-gray-700 mb-2">Fogyás vs. klinikai vizsgálat</h2>
                {clinicalEmptyReason === "missing_therapy_reason" ? (
                  <p className="text-sm text-gray-400">
                    Az összehasonlításhoz add meg a Profilban, miért kaptad a terápiát.{" "}
                    <Link to="/profil" className="text-emerald-600 underline">Profil megnyitása</Link>
                  </p>
                ) : (
                  <p className="text-sm text-gray-400">
                    Az összehasonlításhoz add meg a készítményt és a terápia okát a Profilban.
                  </p>
                )}
              </>
            ) : (
              <>
                <div className="flex items-center justify-between mb-3">
                  <h2 className="text-sm font-semibold text-gray-700">Fogyás vs. {clinicalBranch.trialName} vizsgálat</h2>
                </div>
                <div className="flex flex-wrap gap-3 mb-3">
                  <div className="flex items-center gap-1">
                    <div className="w-4 h-0.5 rounded bg-gray-400" />
                    <span className="text-xs text-gray-500">{formatComparatorLabel(clinicalBranch.comparatorLabel, IS_NATIVE)}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <div className="w-4 h-0.5 rounded bg-blue-600" />
                    <span className="text-xs text-gray-500">{formatReferenceDoseLabel(matchedDose.doseMg, IS_NATIVE)}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <div className="w-4 h-0.5 rounded bg-black" />
                    <span className="text-xs text-gray-500">Te</span>
                  </div>
                </div>
                <ResponsiveContainer width="100%" height={220}>
                  <LineChart>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="week" type="number" tick={{ fontSize: 10 }} tickFormatter={formatWeekTick} interval="preserveStartEnd" />
                    <YAxis tick={{ fontSize: 10 }} width={50} tickFormatter={formatPercentTick} />
                    <Tooltip formatter={(v: any) => formatPercentTick(Number(v))} labelFormatter={(label: any) => formatWeekTick(Number(label))} contentStyle={{ fontSize: 12, borderRadius: 8 }} />
                    <Line data={comparatorSeries} type="monotone" dataKey="pct" stroke="#9CA3AF" strokeWidth={2} dot={false} name={formatComparatorLabel(clinicalBranch.comparatorLabel, IS_NATIVE)} />
                    <Line data={referenceSeries} type="monotone" dataKey="pct" stroke="#2563EB" strokeWidth={2} dot={false} name={formatReferenceDoseLabel(matchedDose.doseMg, IS_NATIVE)} />
                    <Line data={userTrialData} type="monotone" dataKey="yourPct" stroke="#111" strokeWidth={2.5} dot={false} name="Te" />
                  </LineChart>
                </ResponsiveContainer>
                <p className="text-xs text-gray-400 mt-2">
                  {clinicalBranch.trialName} vizsgálat · {clinicalBranch.weeks} hét · {clinicalBranch.populationLabel}.
                  {clinicalBranch.derived && formatDerivedNote(IS_NATIVE)}
                  {" "}A köztes értékek interpoláltak.{" "}
                  <button type="button" onClick={() => openExternal("/tudastar/")} className="text-emerald-600 underline">Részletek és forrás →</button>
                </p>
                <p className="text-xs text-gray-400 mt-1">Csak tájékoztató.</p>
              </>
            )}
          </div>
        </>
      )}

      {/* ─ Éhomi vércukor — csak ha bekapcsolt és van adat ─
          SZÁNDÉKOSAN a súly-blokkon KÍVÜL (B3, 2026-10-14): a csak vércukrot
          naplózó user (D2) súly nélkül is lássa a görbéjét. */}
      {profil.glucoseEnabled && glucoseData.length > 0 && (
        <div className="bg-white rounded-2xl p-4 border border-gray-100 shadow-sm">
          <h2 className="text-sm font-semibold text-gray-700 mb-3">Éhomi vércukor</h2>
          <ResponsiveContainer width="100%" height={200}>
            <LineChart data={glucoseData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="date" tick={{ fontSize: 10 }} interval="preserveStartEnd" />
              <YAxis domain={["dataMin - 0.5", "dataMax + 0.5"]} tick={{ fontSize: 10 }} width={64} tickFormatter={formatGlucoseTick} />
              <Tooltip
                contentStyle={{ fontSize: 12, borderRadius: 8, border: "1px solid #e5e7eb" }}
                formatter={(v: any) => [formatGlucoseTick(v), "Éhomi vércukor"]}
              />
              <Line type="monotone" dataKey="glucose" stroke="#475569" strokeWidth={2.5} dot={{ r: 3 }} />
            </LineChart>
          </ResponsiveContainer>
          <p className="text-xs text-gray-400 mt-2">
            Az értékeket tájékoztatásul rögzíted és jelenítheted meg — az értelmezésükről a kezelőorvosoddal egyeztess.
          </p>
        </div>
      )}
    </div>
  );
}
